import * as z from "zod";

/**
 * A CTA Redo builds itself, so the merchant only chooses whether to send it.
 * `enabled` is an explicit opt-in — absent means off.
 */
export const DeflectionCtaToggleSchema = z.object({
  enabled: z.boolean().nullish(),
});
export type DeflectionCtaToggle = z.infer<typeof DeflectionCtaToggleSchema>;

/**
 * A CTA whose destination only the merchant knows. Merchant free text, stored
 * normalized: pass through, NEVER fetch.
 */
export const DeflectionCtaLinkSchema = z.object({
  url: z.string().nullish(),
  enabled: z.boolean().nullish(),
});
export type DeflectionCtaLinkSetting = z.infer<typeof DeflectionCtaLinkSchema>;

/** Our key → the §5.6.1.9 `name` we send for it. */
export const DEFLECTION_CTA_NAMES = {
  viewOrder: "VIEW_ORDER",
  requestRefund: "REQUEST_REFUND",
  buyAgain: "BUY_AGAIN",
  writeReview: "WRITE_REVIEW",
} as const;

export const DEFLECTION_CTA_KEYS = [
  "viewOrder",
  "requestRefund",
  "buyAgain",
  "writeReview",
] as const;
export type DeflectionCtaKey = (typeof DEFLECTION_CTA_KEYS)[number];

/** Built by Redo at send time, so these carry a toggle and no merchant url. */
export const DEFLECTION_DERIVED_CTA_KEYS: ReadonlySet<DeflectionCtaKey> =
  new Set(["viewOrder", "buyAgain"]);

/**
 * `requestRefund` carries a url only for merchants NOT on Redo returns — with
 * Redo returns we build their portal link per order and ignore any stored url.
 */
export const DeflectionCtaLinksSchema = z.object({
  viewOrder: DeflectionCtaToggleSchema.nullish(),
  requestRefund: DeflectionCtaLinkSchema.nullish(),
  buyAgain: DeflectionCtaToggleSchema.nullish(),
  writeReview: DeflectionCtaLinkSchema.nullish(),
});
export type DeflectionCtaLinks = z.infer<typeof DeflectionCtaLinksSchema>;

/**
 * Merchant config for the Disputely RESOLVE response, at
 * `settings.disputes.deflection` — distinct from evidence-facing `reclaimProfile`.
 */
export const DeflectionSettingsSchema = z.object({
  callToActionLinks: DeflectionCtaLinksSchema.nullish(),
});
export type DeflectionSettings = z.infer<typeof DeflectionSettingsSchema>;

/** Parse a stored value (loose Mongo read), or undefined if malformed. */
export function parseDeflectionSettings(
  value: unknown,
): DeflectionSettings | undefined {
  if (value == null) {
    return undefined;
  }
  const parsed = DeflectionSettingsSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}
